import {
  forwardRef,
  ForwardRefRenderFunction,
  ReactElement,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Autocomplete, AutocompleteProps, AutocompleteRef } from '@shared/components/autocomplete';
import { IndexResponse } from '@shared/types/services';
import { cancelQueries, clearIndexQuery, QueryFactory, useQuery } from '@shared/utils/http-client';

import { styles } from './Lookup.styles';

// ******************************************
// * !! DO NOT USE COMPONENT DIRECTLY    !! *
// * !! USE *SingleLookup / *MultiLookup !! *
// ******************************************

export interface LookupProps
  extends Omit<
      AutocompleteProps,
      'classes' | 'list' | 'loading' | 'searchValue' | 'onClearList' | 'onGetList' | 'onSearchValueChange'
    >,
    WithStyles<typeof styles> {
  localSearch: string;
  placeholder: string;
  queryFactory: QueryFactory<IndexResponse<any>>;
  queryParams?: Query;
  setSearchOnSelect?: boolean;
  getActionRow?: (search: string, clearSearch: () => void, empty: boolean) => ReactElement | undefined;
  onChangeSearch?: (search: string) => void;
  setLocalSearch: (search: string) => void;
}

// ******************************************
// * !! DO NOT USE COMPONENT DIRECTLY    !! *
// * !! USE *SingleLookup / *MultiLookup !! *
// ******************************************

const LookupComponent: ForwardRefRenderFunction<AutocompleteRef, LookupProps> = (
  {
    classes,
    localSearch,
    queryFactory,
    queryParams,
    placeholder,
    setSearchOnSelect,
    getActionRow,
    onChangeSearch,
    setLocalSearch,
    ...autocompleteProps
  },
  ref
) => {
  const autocompleteRef = useRef<AutocompleteRef>(null);
  const [querySearch, setQuerySearch] = useState<string>(localSearch);

  useImperativeHandle<AutocompleteRef, AutocompleteRef>(ref, () => ({
    ...(autocompleteRef.current as AutocompleteRef),
    clear: () => {
      setLocalSearch('');
      autocompleteRef?.current?.clear();
    },
  }));

  const queryOptions = useMemo(
    () => queryFactory({ query: { ...queryParams, search: querySearch } }),
    [queryFactory, queryParams, querySearch]
  );
  const queryData = useQuery({ ...queryOptions, enabled: !!querySearch });

  const items = useMemo(() => (querySearch ? queryData.data?.data ?? [] : []), [queryData.data, querySearch]);

  const actionRow = useMemo(
    () => (getActionRow ? getActionRow(localSearch.trim(), () => setLocalSearch(''), items.length === 0) : undefined),
    [getActionRow, items, localSearch]
  );

  return (
    <Autocomplete
      {...autocompleteProps}
      ref={autocompleteRef}
      actionRow={actionRow}
      classes={{
        root: classes.root,
        actionRow: classes.actionRow,
        labelRoot: classes.labelRoot,
        labelError: classes.labelError,
        list: classes.list,
        listItem: classes.listItem,
        listItemDisabled: classes.listItemDisabled,
        notFoundResults: classes.notFoundResults,
        notFoundText: classes.notFoundText,
        optionsList: classes.optionsList,
      }}
      clearAfterSelect
      hideEmpty={autocompleteProps.hideEmpty ?? !!actionRow}
      inputProps={{
        ...autocompleteProps.inputProps,
        placeholder: placeholder,
        withSearchIcon: true,
      }}
      list={items}
      loading={queryData.isFetching}
      searchValue={localSearch}
      withClearIcon
      onGetList={setQuerySearch}
      onClearList={() => {
        setQuerySearch('');
        cancelQueries({ queryKey: queryOptions.queryKey });
        clearIndexQuery(queryFactory, { ...queryParams });
      }}
      onSearchValueChange={(search) => {
        setLocalSearch(search);

        if (onChangeSearch) {
          onChangeSearch(search);
        }
      }}
      onSelect={(item) => {
        if (autocompleteProps.onSelect) autocompleteProps.onSelect(item);

        if (setSearchOnSelect) {
          setLocalSearch(typeof item.name === 'string' ? item.name : '');
        } else {
          setLocalSearch('');
        }
      }}
    />
  );
};

export const Lookup = withStyles(styles)(forwardRef(LookupComponent));
